import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import ControlHorariLayout from "./ControlHorariLayout";
import "./controlhorari.css";

// Contingut d'ajuda contextual del portal mòbil. Cada tema correspon a una
// pantalla (o pas d'una pantalla) i s'obre des del botó "?" de la capçalera
// (ControlHorariLayout, prop `ajuda`) o des del menú hamburger ("inici").
// Pantalla purament informativa: no consulta cap dada ni modifica res.
const TEMES = {
  inici: {
    titol: "Ajuda · Inici",
    seccions: [
      {
        titol: "Què veus en aquesta pantalla",
        paragrafs: [
          "La pantalla d'Inici mostra l'estat actual de la teva jornada: si has fitxat o no, des de quan estàs treballant o en pausa, el temps efectiu acumulat avui i l'horari previst.",
        ],
      },
      {
        titol: "Temps efectiu, restant i extra",
        paragrafs: [
          "\"Temps efectiu\" és el temps net que portes treballat avui (no compta les pauses que no computen com a treball).",
          "Si tens un horari previst, al costat es mostra quant et falta per completar-lo (\"Temps restant\") o quant portes de més un cop superat (\"Temps extra\").",
        ],
      },
      {
        titol: "Horari previst avui",
        paragrafs: [
          "Mostra les franges que hauries de treballar avui segons el teu horari i el calendari laboral.",
          "Si una absència parcial te'l retalla, apareix indicat amb \"Retallat per: ...\". Si no tens cap horari avui (festiu, vacances, baixa, fora de la relació laboral, etc.), es mostra \"Sense horari\" amb el motiu.",
        ],
      },
      {
        titol: "Com fitxar",
        llista: [
          "A la part inferior veuràs només els botons vàlids segons el teu estat.",
          "Entrada: fitxa quan comencis la jornada.",
          "Inici de pausa: inicia-la quan deixis de treballar i tria el tipus correcte.",
          "Fi de pausa: prem-lo quan tornis a treballar. Mentre hi hagi una pausa oberta no pots fer Sortida.",
          "Sortida: fitxa quan acabis la jornada.",
          "Si la jornada passa de mitjanit: continua amb la mateixa jornada; no facis una nova entrada.",
          "Si t'equivoques: no intentis compensar-ho amb un altre fitxatge; comunica-ho perquè es corregeixi des d'Administració.",
          "Fitxa sempre el que realment fas.",
          "No facis una nova Entrada per corregir un error anterior.",
        ],
      },
      {
        titol: "Sense connexió",
        paragrafs: [
          "Pots continuar fitxant encara que no tinguis xarxa: el registre es guarda al dispositiu i se sincronitza automàticament quan torni la connexió. El comptador de dalt de tot indica quants registres estan pendents de sincronitzar.",
        ],
      },
      {
        titol: "Altres pantalles",
        paragrafs: [
          "Històric: consulta les jornades ja tancades, mes a mes.",
          "Registres: totals d'hores (objectiu, treballades, pendents/excedents) per al període que triïs.",
          "Al menú (icona ≡, a dalt a l'esquerra) trobaràs també aquesta Ajuda i l'opció per tancar sessió.",
        ],
      },
    ],
  },
  fitxar: {
    titol: "Ajuda · Fitxar",
    seccions: [
      {
        titol: "Els tipus de fitxatge",
        llista: [
          "Entrada: fitxa quan comencis la jornada (botó a la pantalla d'Inici).",
          "Inici de pausa: fitxa quan deixis de treballar temporalment i tria el tipus de pausa correcte.",
          "Fi de pausa: fitxa quan tornis a treballar després d'una pausa.",
          "Sortida: fitxa quan acabis definitivament la jornada (botó a la pantalla d'Inici).",
        ],
      },
      {
        titol: "Només veuràs les opcions vàlides",
        paragrafs: [
          "La pantalla d'Inici només mostra els botons que té sentit prémer segons el teu estat real: per exemple, no pots fer \"Fi de pausa\" si no hi ha cap pausa oberta, ni \"Sortida\" mentre n'hi ha una d'oberta.",
        ],
      },
      {
        titol: "Si la jornada passa de mitjanit",
        paragrafs: [
          "Continua amb la mateixa jornada: no facis una nova Entrada encara que hagi canviat el dia. El sistema ja associa correctament les hores treballades a la jornada oberta.",
        ],
      },
      {
        titol: "Si t'equivoques",
        paragrafs: [
          "No intentis compensar-ho fent un altre fitxatge. Fitxa sempre el que realment fas i comunica l'error perquè es corregeixi des d'Administració.",
        ],
      },
      {
        titol: "Sense connexió",
        paragrafs: [
          "Pots fitxar igualment: es guarda en aquest dispositiu i se sincronitza automàticament quan torni la connexió.",
        ],
      },
    ],
  },
  tipusPausa: {
    titol: "Ajuda · Tipus de pausa",
    seccions: [
      {
        titol: "Per a què serveix",
        paragrafs: [
          "Quan fitxes \"Inici de pausa\" has de triar quin tipus de pausa fas (per exemple dinar, descans, gestions personals...). Els tipus disponibles els configura la teva Empresa.",
        ],
      },
      {
        titol: "\"Computa com a temps de treball\"",
        paragrafs: [
          "Sota cada tipus de pausa hi surt indicat si computa o no com a temps de treball.",
          "Si computa: aquell temps es compta com a treballat, encara que estiguis de pausa.",
          "Si no computa: aquell temps es resta de les hores treballades del dia (per exemple, un dinar llarg no retribuït).",
        ],
      },
      {
        titol: "Tria-ho amb cura",
        paragrafs: [
          "Un cop selecciones el tipus de pausa, el \"Fi de pausa\" es registrarà automàticament amb el mateix tipus, així que és important triar-lo correctament a l'inici.",
        ],
      },
    ],
  },
  historic: {
    titol: "Ajuda · Històric",
    seccions: [
      {
        titol: "Què veus en aquesta pantalla",
        paragrafs: [
          "L'Històric mostra, mes a mes, totes les jornades ja registrades: dia a dia, amb cada fitxatge (Entrada, Pauses, Sortida) i l'hora exacta en què es va fer.",
        ],
      },
      {
        titol: "\"En jornada\" o \"Tancada\"",
        paragrafs: [
          "Cada dia mostra una etiqueta: \"Tancada\" si ja hi ha una Sortida registrada, o \"En jornada\" si encara està oberta (per exemple, si encara no has fitxat la sortida d'una jornada que passa de mitjanit).",
        ],
      },
      {
        titol: "Navegació",
        paragrafs: [
          "Amb les fletxes de dalt pots moure't a mesos anteriors o posteriors per revisar l'històric complet.",
        ],
      },
    ],
  },
  registres: {
    titol: "Ajuda · Registres",
    seccions: [
      {
        titol: "Què veus en aquesta pantalla",
        paragrafs: [
          "Registres calcula, per al període de dates que triïs (\"Des de\" / \"Fins a\"), els totals d'hores del període: Objectiu (les que hauries de treballar), Treballat (les que realment has fet) i, segons el cas, Excedent (si has treballat de més) o Pendent (si encara et falten hores).",
        ],
      },
      {
        titol: "Desglossament per dia",
        paragrafs: [
          "A sota trobaràs el detall dia a dia amb el Treballat, el Previst i el Saldo (la diferència entre els dos, en positiu si vas per davant i en negatiu si et falten hores).",
        ],
      },
      {
        titol: "Avisos sobre el període",
        paragrafs: [
          "Si el període consultat cau totalment o parcialment fora de la teva relació laboral (per exemple, abans de la data d'alta), apareix un avís i no es compten hores previstes en aquells dies.",
        ],
      },
    ],
  },
};

function ControlHorariAjuda() {
  const { tema } = useParams();
  const navigate = useNavigate();
  const contingut = TEMES[tema] || TEMES.inici;

  return (
    <ControlHorariLayout titol="Ajuda">
      <div className="ch-ajuda">
        <button type="button" className="ch-boto-secundari ch-boto-tornar" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faChevronLeft} /> Tornar
        </button>

        <p className="ch-pregunta ch-ajuda-titol">{contingut.titol}</p>

        {contingut.seccions.map((seccio) => (
          <div className="ch-targeta" key={seccio.titol}>
            <div className="ch-targeta-capcalera">
              <span>{seccio.titol}</span>
            </div>
            {seccio.paragrafs?.map((p) => (
              <p className="ch-text-suau ch-ajuda-paragraf" key={p}>
                {p}
              </p>
            ))}
            {seccio.llista && (
              <ul className="ch-ajuda-llista">
                {seccio.llista.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </ControlHorariLayout>
  );
}

export default ControlHorariAjuda;
